import React from "react";
import { Tag } from "primereact/tag";
import { WeekSchedule, DaySchedule } from "@types";

interface ScheduleDisplayProps {
  schedule: WeekSchedule;
  showRawData?: boolean;
}

const DAYS = [
  { key: "monday", label: "Monday", abbr: "Mon", icon: "📆", type: "weekday" },
  { key: "tuesday", label: "Tuesday", abbr: "Tue", icon: "📆", type: "weekday" },
  { key: "wednesday", label: "Wednesday", abbr: "Wed", icon: "📆", type: "weekday" },
  { key: "thursday", label: "Thursday", abbr: "Thu", icon: "📆", type: "weekday" },
  { key: "friday", label: "Friday", abbr: "Fri", icon: "📆", type: "weekday" },
  { key: "saturday", label: "Saturday", abbr: "Sat", icon: "📅", type: "weekend" },
  { key: "sunday", label: "Sunday", abbr: "Sun", icon: "📅", type: "weekend" },
] as const;

function formatTime(time: string): string {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}

function DayScheduleDisplay({ day, schedule, icon, type }: {
  day: string;
  schedule: DaySchedule;
  icon: string;
  type: "weekday" | "weekend";
}) {
  const getStatusTag = () => {
    if (!schedule || !schedule.isOpen) {
      return <Tag severity="danger" value="CLOSED" icon="pi pi-times" />;
    }
    if (schedule.is24Hours) {
      return <Tag severity="success" value="24 HOURS" icon="pi pi-clock" />;
    }
    return <Tag severity="info" value="OPEN" icon="pi pi-check" />;
  };

  const getHoursDisplay = () => {
    if (!schedule || !schedule.isOpen) {
      return (
        <span className="text-red-600 font-semibold">
          ❌ Closed
        </span>
      );
    }
    if (schedule.is24Hours) {
      return (
        <span className="text-green-600 font-bold">
          🔄 Open 24 Hours
        </span>
      );
    }
    if (schedule.openTime && schedule.closeTime) {
      const formattedOpen = formatTime(schedule.openTime);
      const formattedClose = formatTime(schedule.closeTime);

      // Calculate if currently open (simplified logic)
      const now = new Date();
      const currentHour = now.getHours();
      const openHour = parseInt(schedule.openTime.split(':')[0]);
      const closeHour = parseInt(schedule.closeTime.split(':')[0]);
      const isCurrentlyOpen = currentHour >= openHour && currentHour < closeHour;

      return (
        <span className={`font-semibold ${isCurrentlyOpen ? 'text-green-700' : 'text-gray-700'}`}>
          🕐 {formattedOpen} - {formattedClose}
          {isCurrentlyOpen && <span className="ml-2 text-xs text-green-600">(Open Now)</span>}
        </span>
      );
    }
    return <span className="text-orange-600 font-semibold">⚠️ Hours not set</span>;
  };

  return (
    <div className={`flex align-items-center justify-content-between p-3 hover:surface-50 border-round transition-all transition-duration-200 ${type === 'weekend' ? 'bg-blue-50' : ''}`}>
      <div className="flex align-items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <span className="font-bold text-900" style={{ minWidth: "100px" }}>{day}</span>
        {getStatusTag()}
      </div>
      <div className="flex align-items-center">
        {getHoursDisplay()}
      </div>
    </div>
  );
}

export function ScheduleDisplay({ schedule, showRawData = false }: ScheduleDisplayProps) {
  if (!schedule) {
    return (
      <div className="surface-card p-4 border-round text-center">
        <i className="pi pi-info-circle text-4xl text-orange-500 mb-3"></i>
        <p className="text-lg font-semibold">No schedule data available</p>
      </div>
    );
  }

  // Calculate statistics
  const stats = {
    openDays: DAYS.filter(d => schedule[d.key as keyof WeekSchedule]?.isOpen).length,
    closedDays: DAYS.filter(d => !schedule[d.key as keyof WeekSchedule]?.isOpen).length,
    twentyFourHourDays: DAYS.filter(d => schedule[d.key as keyof WeekSchedule]?.is24Hours).length,
  };

  // Group days by schedule pattern
  const weekdaySchedule = DAYS.slice(0, 5).map(d => schedule[d.key as keyof WeekSchedule]);
  const hasUniformWeekdays = weekdaySchedule.every((s, _, arr) =>
    s?.isOpen === arr[0]?.isOpen &&
    s?.openTime === arr[0]?.openTime &&
    s?.closeTime === arr[0]?.closeTime &&
    s?.is24Hours === arr[0]?.is24Hours
  );

  return (
    <div className="schedule-display">
      {/* Header with Summary */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-4 border-round-top">
        <h4 className="mt-0 mb-2">📍 Location Schedule Overview</h4>
        <div className="flex justify-content-between align-items-center">
          <span className="text-sm opacity-90">Full week schedule with {stats.openDays} operating days</span>
          {hasUniformWeekdays && (
            <Tag severity="warning" value="Uniform Weekdays" icon="pi pi-check-circle" />
          )}
        </div>
      </div>

      {/* Statistics Bar */}
      <div className="surface-100 p-3 border-x-1 border-200">
        <div className="flex justify-content-around text-center">
          <div>
            <div className="text-3xl font-bold text-green-600">{stats.openDays}</div>
            <div className="text-sm text-700 font-semibold">✅ Open Days</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-red-600">{stats.closedDays}</div>
            <div className="text-sm text-700 font-semibold">❌ Closed Days</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-600">{stats.twentyFourHourDays}</div>
            <div className="text-sm text-700 font-semibold">🔄 24hr Days</div>
          </div>
        </div>
      </div>

      {/* Weekdays Section */}
      <div className="surface-card border-x-1 border-200">
        <div className="px-3 py-2 bg-gray-50 border-bottom-1 border-200">
          <h5 className="m-0 text-700">💼 Weekdays (Monday - Friday)</h5>
        </div>
        {DAYS.slice(0, 5).map((day, index) => (
          <React.Fragment key={day.key}>
            {index > 0 && <div className="border-top-1 surface-border mx-3" />}
            <DayScheduleDisplay
              day={day.label}
              schedule={schedule[day.key as keyof WeekSchedule]}
              icon={day.icon}
              type={day.type}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Weekend Section */}
      <div className="surface-card border-x-1 border-bottom-1 border-round-bottom border-200">
        <div className="px-3 py-2 bg-blue-50 border-bottom-1 border-top-1 border-200">
          <h5 className="m-0 text-700">🌟 Weekend (Saturday - Sunday)</h5>
        </div>
        {DAYS.slice(5).map((day, index) => (
          <React.Fragment key={day.key}>
            {index > 0 && <div className="border-top-1 surface-border mx-3" />}
            <DayScheduleDisplay
              day={day.label}
              schedule={schedule[day.key as keyof WeekSchedule]}
              icon={day.icon}
              type={day.type}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Gate Access Info */}
      <div className="bg-orange-50 p-3 border-round-bottom mt-2">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-lock text-orange-600"></i>
          <span className="font-semibold text-orange-800">🚪 Gate Access: Available 24/7 with access code</span>
        </div>
      </div>
    </div>
  );
}